import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { tokens } from "@core/theme/tokens";

// SPEC-218 §4.1, PA-036-B: lista blanca cerrada que el editor intenta
// emitir. No es la barrera real — `api/src/lib/restricted-rich-text.ts`
// vuelve a sanear al guardar/previsualizar/probar — pero sin esto lo que el
// usuario ve en pantalla (negrita vía `<b>` de `execCommand`, por ejemplo)
// no sobreviviría al saneado del servidor y dejaría de coincidir con lo
// guardado.
const ALLOWED_TAGS = new Set([
  "P",
  "BR",
  "STRONG",
  "EM",
  "A",
  "UL",
  "OL",
  "LI",
]);
const TAG_ALIASES: Readonly<Record<string, string>> = { B: "STRONG", I: "EM" };

function normalizeNode(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as HTMLElement;
    normalizeNode(el);
    const alias = TAG_ALIASES[el.tagName];
    if (alias) {
      const replacement = document.createElement(alias);
      while (el.firstChild) replacement.appendChild(el.firstChild);
      el.replaceWith(replacement);
      continue;
    }
    if (!ALLOWED_TAGS.has(el.tagName)) {
      // Etiqueta fuera de la lista blanca (p. ej. `<div>` que algunos
      // navegadores insertan en `execCommand`): se conserva el texto, se
      // descarta el elemento — mismo criterio que
      // `sanitizeRestrictedHtml` del servidor.
      const parent = el.parentNode;
      if (parent) {
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      }
      continue;
    }
    if (el.tagName === "A") {
      const href = el.getAttribute("href") ?? "";
      for (const attr of Array.from(el.attributes)) {
        if (attr.name !== "href") el.removeAttribute(attr.name);
      }
      if (!/^(https?:|mailto:)/i.test(href)) el.removeAttribute("href");
    } else {
      for (const attr of Array.from(el.attributes))
        el.removeAttribute(attr.name);
    }
  }
}

// Se corre antes de guardar/previsualizar/probar, nunca en cada tecla:
// reescribir `innerHTML` del `contentEditable` en cada `input` movería el
// cursor mientras el usuario escribe.
export function normalizeRichText(html: string): string {
  const container = document.createElement("div");
  container.innerHTML = html;
  normalizeNode(container);
  return container.innerHTML;
}

export function isEmptyRichText(html: string): boolean {
  const container = document.createElement("div");
  container.innerHTML = html;
  return (container.textContent ?? "").trim().length === 0;
}

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}

// PA-036-B: editor propio sobre `contenteditable`, sin dependencias nuevas.
// Barra con exactamente lo que autoriza SPEC-218 §4.1: párrafos (Enter),
// negrita, cursiva, enlace y lista. Nada de tamaños, colores, imágenes ni
// HTML crudo.
export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(function RichTextEditor(
  { value, onChange, disabled = false, ariaLabel },
  ref,
) {
  const editorRef = useRef<HTMLDivElement>(null);
  // `null` (nunca un valor real de `value`) como centinela: fuerza a que el
  // primer render SIEMPRE pinte el DOM. Con `useRef(value)` el guardia de
  // abajo ya salía en falso en el montaje (`value === lastEmitted.current`)
  // y el cuerpo persistido de una plantilla existente nunca llegaba a
  // `innerHTML` — el editor abría vacío aunque `access_token`,
  // `referral_progressed` u `offer_request` tuvieran contenido real
  // (design-reviewer, intento 2, RichTextEditor.tsx:98).
  const lastEmitted = useRef<string | null>(null);

  useEffect(() => {
    // Solo pisa el DOM cuando el valor externo cambió por fuera de este
    // componente (cambio de idioma o de plantilla, o el montaje inicial) —
    // nunca en cada `onInput`, para no mover el cursor mientras el usuario
    // escribe.
    if (editorRef.current && value !== lastEmitted.current) {
      editorRef.current.innerHTML = value;
      lastEmitted.current = value;
    }
  }, [value]);

  useEffect(() => {
    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
      // Sin `execCommand` (jsdom en pruebas unitarias): el editor sigue
      // funcionando como `contenteditable` simple.
    }
  }, []);

  const emit = () => {
    const html = editorRef.current?.innerHTML ?? "";
    lastEmitted.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string) => {
    editorRef.current?.focus();
    try {
      document.execCommand(command, false, arg);
    } catch {
      // Ídem: sin `execCommand` el botón queda sin efecto en pruebas.
    }
    emit();
  };

  useImperativeHandle(ref, () => ({
    // SPEC-218 §4.2, criterio insertable: los parámetros se insertan en la
    // posición del cursor del cuerpo del idioma activo, no se escriben a
    // mano.
    insertText: (text: string) => exec("insertText", text),
  }));

  const insertLink = () => {
    const url = window.prompt("URL del enlace (http, https o mailto)");
    if (!url) return;
    exec("createLink", url);
  };

  return (
    <div>
      <div
        role="toolbar"
        aria-label={`Formato de ${ariaLabel}`}
        style={{ display: "flex", gap: 4, marginBottom: 6 }}
      >
        <button
          type="button"
          className="chip"
          onClick={() => exec("bold")}
          disabled={disabled}
          aria-label="Negrita"
        >
          <strong>N</strong>
        </button>
        <button
          type="button"
          className="chip"
          onClick={() => exec("italic")}
          disabled={disabled}
          aria-label="Cursiva"
        >
          <em>C</em>
        </button>
        <button
          type="button"
          className="chip"
          onClick={insertLink}
          disabled={disabled}
          aria-label="Enlace"
        >
          Enlace
        </button>
        <button
          type="button"
          className="chip"
          onClick={() => exec("insertUnorderedList")}
          disabled={disabled}
          aria-label="Lista"
        >
          Lista
        </button>
      </div>
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        aria-disabled={disabled}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        style={{
          minHeight: 110,
          padding: "11px 13px",
          borderRadius: 11,
          border: `1px solid ${tokens.surface.line2}`,
          background: "rgba(255,255,255,0.03)",
          color: tokens.text.primary,
          font: `400 13.5px/1.5 ${tokens.font.ui}`,
        }}
      />
    </div>
  );
});
